import { useEffect, useState } from "react";
import { localDayKey } from "@/lib/format";
import type { LocalNow } from "@/lib/memoListGroups";

function readNow(): LocalNow {
  const d = new Date();
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return { day: localDayKey(d), time: `${hh}:${mm}` };
}

/**
 * The viewer's local day + minute ("2026-10-05" / "18:41") that keeps itself current:
 * a timer fires on every minute boundary, and returning to the tab (visibility /
 * focus) re-reads it — timers can fire late or not at all while a laptop sleeps.
 * Without this a page left open would keep a 10:00 meeting under "Anstehend" all day,
 * or yesterday as "Heute".
 */
export function useLocalNow(): LocalNow {
  const [now, setNow] = useState(readNow);

  useEffect(() => {
    const refresh = () =>
      setNow((prev) => {
        const next = readNow();
        // Same minute → keep the old object so React bails out of the re-render.
        return next.day === prev.day && next.time === prev.time ? prev : next;
      });

    // 🔴 The timer re-arms ITSELF. Re-arming from a state change instead (effect deps
    // [now]) stops ticking for good the first time a tick lands in the same minute —
    // e.g. after the OS steps the clock back — because the bail-out above means no
    // re-render, so no new timer.
    let timer = 0;
    const arm = () => {
      const d = new Date();
      const msToNextMinute = 60_000 - (d.getSeconds() * 1000 + d.getMilliseconds()) + 50;
      timer = window.setTimeout(() => {
        refresh();
        arm();
      }, msToNextMinute);
    };
    refresh(); // the minute may have rolled over between render and this effect
    arm();

    const onVisible = () => {
      if (document.visibilityState === "visible") refresh();
    };
    document.addEventListener("visibilitychange", onVisible);
    window.addEventListener("focus", refresh);
    return () => {
      window.clearTimeout(timer);
      document.removeEventListener("visibilitychange", onVisible);
      window.removeEventListener("focus", refresh);
    };
  }, []);

  return now;
}
